import { TradeEntity } from '@/common/types/entities';
import { useMilestoneStore } from '@/core/milestones/milestoneStore';
import type { MilestoneStore } from '@/core/milestones/types';
import { useOrderDependencyStore } from '@/features/orderDependencies/orderDependencyStore';
import type { OrderDependencyStore } from '@/features/orderDependencies/types';
import { useOrderStore } from '@/features/orders/orderStore';
import { getDisplayedOrderEndDate } from '@/features/orders/orderUtils';
import type { OrderStore } from '@/features/orders/types';
import { usePauseStore } from '@/features/pauses/pauseStore';
import type { PauseStore } from '@/features/pauses/types';
import { useProjectStore } from '@/features/projects/projectStore';
import type { ProjectStore } from '@/features/projects/projectTypes';
import { useProjectTradeStore } from '@/features/projectTrades/projectTradeStore';
import { ProjectTradeStore } from '@/features/projectTrades/types';
import {
  SortingDependency,
  SortingElement,
  SortingProps,
} from '@/features/schedule/composables/inRowSorting';
import { NodeName } from '@/repositories/utils/cache';
import { checkIfEventIsFilteredOut } from '@/services/store/schedule/actions/filter';

export function useOrderScheduleDefaultInRowSorting() {
  return (aId: string, bId: string) => {
    const stores = {
      orderStore: useOrderStore(),
      orderDependencyStore: useOrderDependencyStore(),
      projectTradeStore: useProjectTradeStore(),
      milestoneStore: useMilestoneStore(),
      pauseStore: usePauseStore(),
      projectStore: useProjectStore(),
    };

    const a = getElement(aId, stores);
    const b = getElement(bId, stores);

    return defaultOrderScheduleInRowSorting(a, b);
  };
}

export function useOrderScheduleInRowSortingProps(sections?: string[]): SortingProps {
  return getOrderScheduleInRowSortingProps(
    {
      orderStore: useOrderStore(),
      orderDependencyStore: useOrderDependencyStore(),
      projectTradeStore: useProjectTradeStore(),
      milestoneStore: useMilestoneStore(),
      pauseStore: usePauseStore(),
      projectStore: useProjectStore(),
    },
    sections,
  );
}

function getOrderScheduleInRowSortingProps(
  stores: {
    orderStore: OrderStore;
    orderDependencyStore: OrderDependencyStore;
    projectTradeStore: ProjectTradeStore;
    milestoneStore: MilestoneStore;
    pauseStore: PauseStore;
    projectStore: ProjectStore;
  },
  sections?: string[],
): SortingProps {
  const elements = new Map<string, SortingElement>();
  const dependencies = new Map<string, SortingDependency>();
  const specialElementGroups = new Map<string, string[]>();

  Array.from(stores.orderStore.orders.values()).forEach((order) => {
    if (sections && !sections.includes(order.wbsSection.id)) {
      return;
    }
    const isFilteredOut = checkIfEventIsFilteredOut(order.id, NodeName.ORDER, true);

    if (isFilteredOut) {
      return;
    }

    if (order.tradeSequenceInstanceId) {
      if (!specialElementGroups.has(order.tradeSequenceInstanceId)) {
        specialElementGroups.set(order.tradeSequenceInstanceId, []);
      }
      specialElementGroups.get(order.tradeSequenceInstanceId)?.push(order.id);
    }

    const element = getElement(order.id, stores);
    elements.set(order.id, {
      id: order.id,
      startDate: element.startDate,
      endDate: element.endDate,
      sectionId: order.wbsSection.id,
    });
  });

  Array.from(stores.orderDependencyStore.dependencies.values()).forEach((dependency) => {
    const fromId = dependency.from.id;
    const toId = dependency.to.id;

    if (elements.has(fromId) && elements.has(toId)) {
      dependencies.set(dependency.id, {
        id: dependency.id,
        from: { id: fromId },
        to: { id: toId },
      });
    }
  });

  return {
    elements: Array.from(elements.values()),
    dependencies: Array.from(dependencies.values()),
    sections,
    specialElementGroups,
  };
}

function getElement(
  elementId: string,
  stores: {
    orderStore: OrderStore;
    projectTradeStore: ProjectTradeStore;
    milestoneStore: MilestoneStore;
    pauseStore: PauseStore;
    projectStore: ProjectStore;
  },
): {
  startDate: Date;
  endDate: Date;
  name: string;
  trade: TradeEntity | undefined;
} {
  const order = stores.orderStore.orders.get(elementId);
  if (order) {
    const isHourly = stores.projectStore.currentProject?.hourlyPlanningEnabled;
    const endDate = getDisplayedOrderEndDate(order, {
      includeDryingBreak: true,
      isHourly: !!isHourly,
    });
    const trade = stores.projectTradeStore.trades.get(order.tenantTradeVariation?.id ?? '');
    return {
      startDate: order.startAt,
      endDate,
      name: order.name,
      trade,
    };
  }

  const milestone = stores.milestoneStore.milestones.get(elementId);
  if (milestone) {
    const trade = stores.projectTradeStore.trades.get(milestone.tenantTradeVariation?.id ?? '');
    return {
      startDate: milestone.date,
      endDate: milestone.date,
      name: milestone.name,
      trade,
    };
  }

  const pause = stores.pauseStore.pauses.get(elementId);
  if (pause) {
    return {
      startDate: pause.start,
      endDate: pause.end,
      name: pause.name,
      trade: undefined,
    };
  }

  throw new Error('Element not found');
}

export function defaultOrderScheduleInRowSorting(
  a: {
    startDate: Date;
    endDate: Date;
    name: string;
    trade: TradeEntity | undefined;
  },
  b: {
    startDate: Date;
    endDate: Date;
    name: string;
    trade: TradeEntity | undefined;
  },
) {
  const sameStart = a.startDate.getTime() === b.startDate.getTime();
  if (!sameStart) {
    return a.startDate.getTime() - b.startDate.getTime();
  }
  const sameEnd = a.endDate.getTime() === b.endDate.getTime();
  if (!sameEnd) {
    return b.endDate.getTime() - a.endDate.getTime();
  }
  const sameTrade = a.trade?.id === b.trade?.id;
  if (!sameTrade) {
    const tradeAName = a.trade?.name || '';
    const tradeBName = b.trade?.name || '';
    return tradeAName.localeCompare(tradeBName);
  }
  return a.name.localeCompare(b.name);
}
